import { useEffect, useState, useRef, useCallback } from 'react'

/**
 * Hook for managing Faye WebSocket connection
 * @param {string} channel - The Faye channel to subscribe to (e.g., "/messages/12345")
 * @param {function} onMessage - Callback function when message is received
 * @returns {object} - { connected, error }
 */
export const useFaye = (channel, onMessage) => {
  const [connected, setConnected] = useState(false)
  const [error, setError] = useState(null)
  const clientRef = useRef(null)
  const subscriptionRef = useRef(null)

  useEffect(() => {
    if (!channel) return

    // Create Faye client
    const client = new window.Faye.Client('https://live.berkutschi.com/faye')
    clientRef.current = client

    // Subscribe to channel
    const subscription = client.subscribe(channel, (data) => {
      if (onMessage) {
        onMessage(data)
      }
    })

    subscriptionRef.current = subscription

    // Connection status handlers
    client.on('transport:up', () => {
      setConnected(true)
      setError(null)
    })

    client.on('transport:down', () => {
      setConnected(false)
    })

    client.on('transport:error', (err) => {
      setError(err)
      setConnected(false)
    })

    // Cleanup on unmount
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.cancel()
      }
      if (clientRef.current) {
        clientRef.current.disconnect()
      }
    }
  }, [channel, onMessage])

  return { connected, error }
}

/**
 * Hook for fetching initial data and managing real-time updates
 * @param {string} fiscodex - The FIS codex for the event
 * @param {string} apiEndpoint - API endpoint to fetch initial data
 * @returns {object} - { data, loading, error, connected }
 */
export const useLiveData = (fiscodex, apiEndpoint) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Fetch initial data
  useEffect(() => {
    if (!fiscodex || !apiEndpoint) return

    const fetchData = async () => {
      try {
        const response = await fetch(apiEndpoint)
        if (!response.ok) {
          throw new Error('Failed to fetch data')
        }
        const json = await response.json()
        setData(json)
        setLoading(false)
      } catch (err) {
        setError(err.message)
        setLoading(false)
      }
    }

    fetchData()
  }, [fiscodex, apiEndpoint])

  // Handle real-time updates
  const handleMessage = useCallback((message) => {
    setData((prevData) => {
      if (!prevData) return message
      
      // Merge new data with existing data
      return {
        ...prevData,
        ...message
      }
    })
  }, [])

  // Subscribe to Faye updates
  const { connected } = useFaye(
    fiscodex ? `/messages/${fiscodex}` : null,
    handleMessage
  )

  // Refetch data when connection is restored
  useEffect(() => {
    if (connected && !data) {
      // Trigger refetch
      setLoading(true)
    }
  }, [connected, data])

  return { data, loading, error, connected, setData }
}
